'use client';

import { useAuth } from '@/contexts/auth-context';
import { isNoiseException, isThirdPartyException } from '@/utils/exception-noise';
import { isWidgetPath, posthogOptionsFor } from '@/lib/analytics/posthog-options';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';

// Init is a no-op when the key is absent or in dev builds, so local sessions
// (including Fast Refresh error bursts) never reach the production project.
if (
  typeof window !== 'undefined' &&
  process.env.NEXT_PUBLIC_POSTHOG_KEY &&
  process.env.NODE_ENV !== 'development'
) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
    // The widget is silent except for exceptions and its one estimate event; the
    // CRM captures the full set. See lib/analytics/posthog-options.ts.
    ...posthogOptionsFor(window.location.pathname),
    // Error tracking: uncaught errors and unhandled promise rejections.
    capture_exceptions: true,
    before_send: (event) => {
      if (event?.event === '$exception' && (isNoiseException(event) || isThirdPartyException(event))) {
        return null;
      }
      return event;
    },
  });
}

// Separate component because useSearchParams must sit under a Suspense
// boundary to avoid forcing client-side rendering of the whole tree.
function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || !posthog.__loaded || isWidgetPath(pathname)) return;
    let url = window.origin + pathname;
    const query = searchParams.toString();
    if (query) {
      url = `${url}?${query}`;
    }
    // URL-normalizing effects re-run this without a real navigation.
    if (url === lastUrlRef.current) return;
    lastUrlRef.current = url;
    posthog.capture('$pageview', { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}

// Links PostHog events to the logged-in platform user so person profiles carry
// the CRM identity. Must be mounted inside AuthProvider. While impersonating,
// the admin's own identity is kept so the impersonated agent's profile stays clean.
export function PostHogIdentify() {
  const { user, isAuthenticated, impersonating } = useAuth();
  // Holds the identified userId — auth-context rebuilds `user` on every session
  // validation, so guard on the id to avoid re-sending $identify each refresh.
  const identifiedRef = useRef<string | null>(null);
  const userId = isAuthenticated ? user?.userId ?? null : null;

  useEffect(() => {
    if (!posthog.__loaded || impersonating) return;

    if (userId) {
      if (identifiedRef.current === userId) return;
      posthog.identify(userId, {
        email: user?.email,
        name: user?.name,
        agencyId: user?.agencyId,
        agencyCountry: user?.agencyCountry,
        userType: user?.userType,
      });
      if (user?.agencyId) {
        posthog.group('agency', user.agencyId, { country: user.agencyCountry });
      }
      identifiedRef.current = userId;
    } else if (identifiedRef.current) {
      // Logout: unlink the device so the next login doesn't merge into this profile.
      posthog.reset();
      identifiedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, impersonating]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PostHogPageView />
      </Suspense>
      {children}
    </PHProvider>
  );
}
